/* BM Pestañas de producto — Biomedicalii
   Cambia de pestaña con clic y con el teclado (flechas, Inicio y Fin).
   La descripción se recorta tras el primer párrafo (o a N líneas) con botón
   "Ver más" / "Ver menos". */
if (!customElements.get('bm-product-tabs')) {
  customElements.define(
    'bm-product-tabs',
    class BmProductTabs extends HTMLElement {
      connectedCallback() {
        this.initMore();

        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        if (!this.tabs.length) return;

        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onClick);
          tab.addEventListener('keydown', this.onKeydown);
        });

        // En el editor de temas, al elegir un bloque se abre su pestaña.
        this.onBlockSelect = (event) => {
          const tab = this.tabs.find((item) => item.dataset.shopifyEditorBlock && this.blockId(item) === event.detail.blockId);
          if (tab) this.select(tab, false);
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      disconnectedCallback() {
        if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
        if (this.moreObserver) this.moreObserver.disconnect();
      }

      initMore() {
        this.mores = Array.from(this.querySelectorAll('[data-more]'));
        if (!this.mores.length) return;

        this.mores.forEach((more) => {
          const button = more.querySelector('[data-more-toggle]');
          if (button) button.addEventListener('click', () => this.toggleMore(more));
          this.measureMore(more);
        });

        // Vuelve a medir al mostrarse la pestaña, al cargar fuentes o al cambiar el ancho.
        if ('ResizeObserver' in window) {
          this.moreObserver = new ResizeObserver((entries) => {
            entries.forEach((entry) => this.measureMore(entry.target.closest('[data-more]')));
          });
          this.mores.forEach((more) => {
            const body = more.querySelector('[data-more-body]');
            [body, ...body.children].forEach((element) => this.moreObserver.observe(element));
          });
        }
      }

      measureMore(more) {
        const body = more.querySelector('[data-more-body]');
        const button = more.querySelector('[data-more-toggle]');
        if (!body || !button || more.classList.contains('is-expanded')) return;
        // Panel oculto: no hay medidas todavía.
        if (!body.offsetParent) return;

        const lines = parseInt(getComputedStyle(more).getPropertyValue('--bm-ptabs-more-lines'), 10) || 3;
        let limit = lines * 24;
        const paragraph = more.dataset.moreMode === 'paragraph' ? this.firstParagraph(body) : null;
        if (paragraph) {
          limit = Math.ceil(paragraph.getBoundingClientRect().bottom - body.getBoundingClientRect().top);
        }
        more.classList.toggle('is-paragraph', Boolean(paragraph));
        body.style.setProperty('--bm-ptabs-more-height', `${limit}px`);

        const isShort = body.scrollHeight <= limit + 4;
        more.classList.toggle('is-short', isShort);
        button.hidden = isShort;
        if (!more.classList.contains('is-ready')) requestAnimationFrame(() => more.classList.add('is-ready'));
      }

      // Primer bloque de texto que no sea título; si no hay, se recorta por líneas.
      firstParagraph(body) {
        return Array.from(body.querySelectorAll('p, li, div, blockquote')).find((element) => {
          if (element.closest('h1, h2, h3, h4, h5, h6, [role="heading"]')) return false;
          if (element.querySelector('p, div, ul, ol, li, blockquote, table, h1, h2, h3, h4, h5, h6')) return false;
          const text = element.textContent.trim();
          if (!text) return false;
          // Un párrafo escrito solo en negritas funciona como título.
          const bold = Array.from(element.querySelectorAll('strong, b'))
            .map((item) => item.textContent)
            .join('')
            .trim();
          return bold.length < text.length;
        });
      }

      toggleMore(more) {
        const body = more.querySelector('[data-more-body]');
        const button = more.querySelector('[data-more-toggle]');
        const label = more.querySelector('[data-more-label]');
        const expand = !more.classList.contains('is-expanded');

        if (expand) {
          more.classList.add('is-expanded');
          body.style.maxHeight = `${body.scrollHeight}px`;
          // Al terminar la animación se libera la altura para que el texto se adapte al ancho.
          const release = () => {
            if (more.classList.contains('is-expanded')) body.style.maxHeight = 'none';
          };
          body.addEventListener('transitionend', release, { once: true });
          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) release();
        } else {
          body.style.maxHeight = `${body.scrollHeight}px`;
          body.getBoundingClientRect();
          more.classList.remove('is-expanded');
          this.measureMore(more);
          body.style.maxHeight = '';

          const top = more.getBoundingClientRect().top;
          if (top < 0) window.scrollTo({ top: window.scrollY + top - 100, behavior: 'smooth' });
        }

        button.setAttribute('aria-expanded', String(expand));
        if (label) label.textContent = expand ? button.dataset.labelLess : button.dataset.labelMore;
      }

      blockId(tab) {
        try {
          return JSON.parse(tab.dataset.shopifyEditorBlock).id;
        } catch (error) {
          return null;
        }
      }

      onClick(event) {
        this.select(event.currentTarget, true);
      }

      onKeydown(event) {
        const index = this.tabs.indexOf(event.currentTarget);
        let next = null;

        switch (event.key) {
          case 'ArrowRight':
            next = this.tabs[(index + 1) % this.tabs.length];
            break;
          case 'ArrowLeft':
            next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
            break;
          case 'Home':
            next = this.tabs[0];
            break;
          case 'End':
            next = this.tabs[this.tabs.length - 1];
            break;
          default:
            return;
        }

        event.preventDefault();
        this.select(next, true);
      }

      select(tab, focus) {
        this.tabs.forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
          const panel = document.getElementById(item.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });

        if (focus) {
          tab.focus({ preventScroll: true });
          // Solo desplaza la fila de pestañas (celular), no la página.
          const list = tab.parentElement;
          const start = tab.offsetLeft - list.offsetLeft;
          if (start < list.scrollLeft || start + tab.offsetWidth > list.scrollLeft + list.clientWidth) {
            list.scrollTo({ left: start - 16, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
